/**
 * Parse lean christmas_offerings from get_public_restaurant_share_bundle
 * and resolve web restaurant menu category modes.
 */

export type WebMenuCategory = "daily" | "christmas";

export type WebMenuCategoryMode =
  | "daily-only"
  | "christmas-only"
  | "both"
  | "empty";

export type ShareChristmasOfferingSummary = {
  id: string;
  packageTitle: string | null;
  priceEur: number | null;
  currency: string;
  pricePerPerson: boolean;
  seasonLabel: string | null;
  validFrom: string | null;
  validTo: string | null;
  publishedAt: string | null;
  derivedAvailability: string | null;
  coverImageUrl: string | null;
  imageCount: number;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function unknownString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function unknownNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function unknownBoolean(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (v === "true" || v === "1") return true;
  if (v === "false" || v === "0") return false;
  return null;
}

function httpsUrl(raw: string | null | undefined): string | null {
  const url = raw?.trim() ?? "";
  if (/^https?:\/\//i.test(url)) return url;
  return null;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseShareChristmasOfferingSummary(
  raw: unknown
): ShareChristmasOfferingSummary | null {
  const o = asRecord(raw);
  if (!o) return null;
  const id = unknownString(o.id)?.trim();
  if (!id || !UUID_RE.test(id)) return null;
  const pricePerPerson = unknownBoolean(o.price_per_person ?? o.pricePerPerson);
  return {
    id,
    packageTitle: unknownString(o.package_title ?? o.packageTitle),
    priceEur: unknownNumber(o.price_eur ?? o.priceEur),
    currency: unknownString(o.currency) ?? "EUR",
    pricePerPerson: pricePerPerson ?? true,
    seasonLabel: unknownString(o.season_label ?? o.seasonLabel),
    validFrom: unknownString(o.valid_from ?? o.validFrom),
    validTo: unknownString(o.valid_to ?? o.validTo),
    publishedAt: unknownString(o.published_at ?? o.publishedAt),
    derivedAvailability: unknownString(
      o.derived_availability ?? o.derivedAvailability
    ),
    coverImageUrl: httpsUrl(
      unknownString(o.cover_image_url ?? o.coverImageUrl)
    ),
    imageCount: Math.max(0, unknownNumber(o.image_count ?? o.imageCount) ?? 0),
  };
}

export function parseShareChristmasOfferings(
  raw: unknown
): ShareChristmasOfferingSummary[] {
  let value: unknown = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  const out: ShareChristmasOfferingSummary[] = [];
  for (const row of value) {
    const parsed = parseShareChristmasOfferingSummary(row);
    if (parsed) out.push(parsed);
  }
  return out;
}

export function resolveWebMenuCategoryMode(
  dailyCount: number,
  christmasCount: number
): WebMenuCategoryMode {
  const daily = dailyCount > 0;
  const christmas = christmasCount > 0;
  if (daily && christmas) return "both";
  if (christmas) return "christmas-only";
  if (daily) return "daily-only";
  return "empty";
}

export function parseWebMenuCategoryParam(
  raw: string | null | undefined
): WebMenuCategory | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase();
  if (value === "christmas" || value === "navidad" || value === "nadal") {
    return "christmas";
  }
  if (
    value === "daily" ||
    value === "dia" ||
    value === "día" ||
    value === "menu" ||
    value === "menú"
  ) {
    return "daily";
  }
  return null;
}

export function resolveInitialWebMenuCategory(
  mode: WebMenuCategoryMode,
  requested: WebMenuCategory | null,
  deepLinkOfferingId: string | null
): WebMenuCategory {
  if (deepLinkOfferingId && (mode === "both" || mode === "christmas-only")) {
    return "christmas";
  }
  if (mode === "christmas-only") return "christmas";
  if (mode === "daily-only" || mode === "empty") return "daily";
  if (requested === "christmas") return "christmas";
  return "daily";
}

/** Prefer HTTPS public offering path; never invent chalkboard routes. */
export function buildWebOfferingPath(offeringId: string): string {
  return `/o/${encodeURIComponent(offeringId)}`;
}
